import { uuid } from '@utils/common';

import { TAsset } from '@interfaces/assets/asset';

import { EParamTypesExtraInfo } from '@components/Map/AssetsInfo/enums';

import { noSubTypeOptions } from '@components/routes/AdminPanel/AssetsList/constants';

interface IAssetsTotalByOwners {
	count: number;
	id: string;
	name: string;
}

export interface IAssetByType {
	id: string;
	name: string;
	count: number;
	iconId: string;
	subTypes: Array<{
		name: string;
		count: number;
	}>;
}

export const calculateAssetsByType = (assets: TAsset[]): IAssetByType[] => {
	const assetTypes: IAssetByType[] = [];

	if (!assets?.length) return assetTypes;

	for (const asset of assets) {
		const assetType = asset.assetType.assetTypeName;
		const assetSubType = asset.assetSubType?.assetSubTypeName;

		const assetTypeIndex = assetTypes.findIndex((item) => item.name === assetType);

		if (assetTypeIndex === -1) {
			assetTypes.push({
				id: asset.assetType.id,
				name: assetType,
				count: 1,
				iconId: asset.assetType.icon.id,
				subTypes: assetSubType
					? [
						{
							name: assetSubType,
							count: 1,
						},
						{
							name: noSubTypeOptions.label,
							count: 0,
						},
					]
					: [
						{
							name: noSubTypeOptions.label,
							count: 1,
						},
					],
			});
		} else {
			assetTypes[assetTypeIndex].count += 1;

			if (assetSubType) {
				const assetSubTypeIndex = assetTypes[assetTypeIndex].subTypes.findIndex(
					(item) => item.name === assetSubType,
				);

				if (assetSubTypeIndex === -1) {
					assetTypes[assetTypeIndex].subTypes.push({
						name: assetSubType,
						count: 1,
					});
				} else {
					assetTypes[assetTypeIndex].subTypes[assetSubTypeIndex].count += 1;
				}
			} else {
				const assetSubTypeIndex = assetTypes[assetTypeIndex].subTypes.findIndex(
					(item) => item.name === noSubTypeOptions.label,
				);

				if (assetSubTypeIndex !== -1) {
					assetTypes[assetTypeIndex].subTypes[assetSubTypeIndex].count += 1;
				}
			}
		}
	}

	return assetTypes.map((item) => {
		return {
			...item,
			subTypes: item.subTypes.sort((prev) => prev.name === noSubTypeOptions.label ? 1 : -1),
		};
	});
};

export const calculateAssetsByOwners = (assets: TAsset[]) => {
	return assets.reduce<Record<string, IAssetsTotalByOwners>>((acc, curr) => {
		if (acc[curr.owner.name as string]) {
			acc[curr.owner.name as string] = {
				...acc[curr.owner.name as string],
				count: acc[curr.owner.name as string].count + 1,
			};
		} else {
			acc[curr.owner.name as string] = {
				name: curr.owner.name as string,
				count: 1,
				id: uuid(),
			};
		}

		return acc;
	}, {});
};

export const getAssetExtraInfo = (paramName: string, asset: TAsset) => {
	switch (paramName) {
		case EParamTypesExtraInfo.siteAccessAvailability:
			return {
				title: paramName,
				info: asset.assetAccessDetails || '',
				files: asset?.assetAccessDetailsFileIds || [],
			};
		case EParamTypesExtraInfo.powerSupplyAvailabilityStatus:
			return {
				title: paramName,
				info: asset.powerSupplyAvailabilityDetails || '',
				files: asset?.powerSupplyAvailabilityDetailsFileIds || [],
			};
		case EParamTypesExtraInfo.backhaulA11yStatus:
			return {
				title: paramName,
				info: asset.backhaulAccessibilityDetails || '',
				files: asset?.backhaulAccessibilityDetailsFileIds || [],
			};
		case EParamTypesExtraInfo.siteAvailabilityStatus:
			return {
				title: paramName,
				info: asset.assetSiteAvailabilityDetails || '',
				files: asset?.assetSiteAvailabilityDetailsFileIds || [],
			};
		case EParamTypesExtraInfo.specialImplementationExistence:
			return {
				title: paramName,
				info: asset.specialImplementationIssueDetails || '',
				files: asset?.specialImplementationIssueDetailsFileIds || [],
			};
		default:
			return null;
	}
};
